import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/api';

// SystemStatusPanel — mirrors the content of the monitor_health.py alert/daily
// email on the admin dashboard. Reads /strategies/api/admin/server-health/
// every 30 seconds. The host cron writes a fresh snapshot every 5 min, so the
// "Last check" age should normally stay below 5 minutes.

const REFRESH_MS = 30000;
const STALE_AGE_SECONDS = 600;   // dashboard goes red if no fresh snapshot in 10m

function fmtAgo(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60)    return `${seconds}s ago`;
  if (seconds < 3600)  return `${Math.floor(seconds / 60)}m ${seconds % 60}s ago`;
  const h = Math.floor(seconds / 3600);
  return `${h}h ${Math.floor((seconds % 3600) / 60)}m ago`;
}

function fmtUTC(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'UTC', hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }) + ' UTC';
  } catch { return iso; }
}

function HealthDot({ health }) {
  const color =
    health === 'healthy'   ? 'var(--tv-bull)' :
    health === 'unhealthy' ? 'var(--tv-bear)' :
    health === 'starting'  ? 'var(--tv-gold)' :
                             'var(--tv-text-muted)';
  return (
    <span style={{
      display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
      background: color, marginRight: 8, verticalAlign: 'middle',
    }} />
  );
}

function Bar({ pct, warn }) {
  const color = pct >= 90 ? 'var(--tv-bear)' : pct >= warn ? 'var(--tv-gold)' : 'var(--tv-bull)';
  return (
    <div style={{
      width: '100%', height: 6, borderRadius: 3,
      background: 'var(--tv-border-lt)', overflow: 'hidden',
    }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', background: color }} />
    </div>
  );
}

export default function SystemStatusPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/strategies/api/admin/server-health/');
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      setData(await res.json());
      setError('');
    } catch (e) {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  if (loading) {
    return <div style={{ padding: 30, textAlign: 'center', color: 'var(--tv-text-muted)' }}>Loading…</div>;
  }
  if (error) {
    return (
      <div style={{
        padding: 12, background: 'var(--tv-bear-tint)', border: '1px solid var(--tv-bear)',
        borderRadius: 6, color: 'var(--tv-bear)', fontSize: 12,
      }}>{error}</div>
    );
  }
  if (!data?.available) {
    return (
      <div>
        <h2 style={{ margin: '0 0 18px', fontSize: 18, fontWeight: 700, color: 'var(--tv-text)' }}>System Status</h2>
        <div style={{
          padding: 30, textAlign: 'center', background: 'var(--tv-surface)',
          border: '1px dashed var(--tv-border)', borderRadius: 8,
          color: 'var(--tv-text-muted)', fontSize: 13,
        }}>
          No server health snapshot yet. The host cron <code>monitor_health.py</code>
          fires every 5 minutes — once the next run lands you'll see container
          health, disk, memory, and alert history here.
          {data?.reason === 'snapshot_table_missing' && (
            <div style={{ marginTop: 12, color: 'var(--tv-gold)' }}>
              ⚠ <code>server_health_snapshot</code> table not yet applied on prod.
              Run the schema in <code>strategies/api/admin_health/server_status_schema.sql</code>.
            </div>
          )}
        </div>
      </div>
    );
  }

  const l = data.latest;
  const stale = l.age_seconds != null && l.age_seconds > STALE_AGE_SECONDS;
  const problems = Array.isArray(l.problems) ? l.problems : [];
  const containers = Array.isArray(l.containers) ? l.containers : [];
  const unhealthyCount = containers.filter(c => c.health === 'unhealthy').length;
  const healthyCount   = containers.filter(c => c.health === 'healthy').length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--tv-text)' }}>System Status</h2>
        <span style={{
          fontSize: 11, color: stale ? 'var(--tv-bear)' : 'var(--tv-text-muted)',
          fontFamily: 'Roboto Mono, monospace', fontWeight: stale ? 700 : 400,
        }}>
          Last check: {fmtAgo(l.age_seconds)} ({fmtUTC(l.created_at)})
          {stale && ' — STALE'}
        </span>
        {l.hostname && (
          <span style={{ fontSize: 11, color: 'var(--tv-text-muted)', fontFamily: 'Roboto Mono, monospace' }}>
            host: {l.hostname}
          </span>
        )}
        <button type="button" onClick={load} style={{
          marginLeft: 'auto', padding: '6px 14px', fontSize: 12, fontWeight: 600,
          border: '1px solid var(--tv-border)', borderRadius: 6, cursor: 'pointer',
          background: 'var(--tv-surface)', color: 'var(--tv-blue)',
        }}>Refresh</button>
      </div>

      {/* Problem banner */}
      {problems.length > 0 && (
        <div style={{
          padding: 14, marginBottom: 18,
          background: 'var(--tv-bear-tint)', border: '1px solid var(--tv-bear)', borderRadius: 6,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tv-bear)', marginBottom: 6, letterSpacing: '0.05em' }}>
            🚨 {problems.length} ACTIVE ISSUE{problems.length === 1 ? '' : 'S'}
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--tv-text)' }}>
            {problems.map((p, i) => <li key={i} style={{ marginBottom: 2 }}>{p}</li>)}
          </ul>
        </div>
      )}

      {problems.length === 0 && (
        <div style={{
          padding: 14, marginBottom: 18,
          background: 'var(--tv-bull-tint)', border: '1px solid var(--tv-bull)', borderRadius: 6,
          fontSize: 12, color: 'var(--tv-bull)', fontWeight: 700,
        }}>
          ✓ All systems healthy at last check
        </div>
      )}

      {/* Resource cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div style={{ padding: '14px 16px', borderRadius: 6, background: 'var(--tv-surface)', border: '1px solid var(--tv-border)' }}>
          <div style={{ fontSize: 10, color: 'var(--tv-text-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Disk</div>
          <div style={{ fontSize: 22, color: 'var(--tv-text)', fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>
            {l.disk_pct != null ? `${parseFloat(l.disk_pct).toFixed(1)}%` : '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tv-text-muted)', marginTop: 2 }}>
            {l.disk_free_gb != null ? `${parseFloat(l.disk_free_gb).toFixed(1)} GB free` : ''}
          </div>
          <div style={{ marginTop: 8 }}>
            <Bar pct={parseFloat(l.disk_pct || 0)} warn={75} />
          </div>
        </div>

        <div style={{ padding: '14px 16px', borderRadius: 6, background: 'var(--tv-surface)', border: '1px solid var(--tv-border)' }}>
          <div style={{ fontSize: 10, color: 'var(--tv-text-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Memory</div>
          <div style={{ fontSize: 22, color: 'var(--tv-text)', fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>
            {l.mem_pct != null ? `${parseFloat(l.mem_pct).toFixed(1)}%` : '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tv-text-muted)', marginTop: 2 }}>
            {l.mem_used_mb && l.mem_total_mb ? `${l.mem_used_mb} / ${l.mem_total_mb} MB` : ''}
          </div>
          <div style={{ marginTop: 8 }}>
            <Bar pct={parseFloat(l.mem_pct || 0)} warn={75} />
          </div>
        </div>

        <div style={{ padding: '14px 16px', borderRadius: 6, background: 'var(--tv-surface)', border: '1px solid var(--tv-border)' }}>
          <div style={{ fontSize: 10, color: 'var(--tv-text-muted)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Containers</div>
          <div style={{ fontSize: 22, color: unhealthyCount > 0 ? 'var(--tv-bear)' : 'var(--tv-text)', fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>
            {healthyCount} / {containers.length}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tv-text-muted)', marginTop: 2 }}>
            healthy {unhealthyCount > 0 && <span style={{ color: 'var(--tv-bear)' }}>· {unhealthyCount} unhealthy</span>}
          </div>
        </div>
      </div>

      {/* Container table */}
      <div style={{ background: 'var(--tv-surface)', border: '1px solid var(--tv-border)', borderRadius: 8, overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch', marginBottom: 24 }}>
        <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--tv-surface-hi)' }}>
            <tr>
              {['Container', 'Health', 'Status'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                  color: 'var(--tv-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em',
                  borderBottom: '1px solid var(--tv-border)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {containers.map((c) => (
              <tr key={c.name} style={{ borderTop: '1px solid var(--tv-border-lt)' }}>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text)', fontWeight: 600 }}>
                  <HealthDot health={c.health} />{c.name}
                </td>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace',
                              color: c.health === 'unhealthy' ? 'var(--tv-bear)' : c.health === 'healthy' ? 'var(--tv-bull)' : 'var(--tv-text-muted)' }}>
                  {(c.health || 'unknown').toUpperCase()}
                </td>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text-dim)' }}>
                  {c.status}
                </td>
              </tr>
            ))}
            {containers.length === 0 && (
              <tr><td colSpan={3} style={{ padding: 20, textAlign: 'center', color: 'var(--tv-text-muted)' }}>No container data in latest snapshot</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recent alerts */}
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: 'var(--tv-text)', letterSpacing: '0.04em' }}>
          Recent alerts (last 10)
        </h3>
      </div>
      <div style={{ background: 'var(--tv-surface)', border: '1px solid var(--tv-border)', borderRadius: 8, overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse', fontSize: 12 }}>
          <thead style={{ background: 'var(--tv-surface-hi)' }}>
            <tr>
              {['When (UTC)', 'Issues', 'Detail'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                  color: 'var(--tv-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em',
                  borderBottom: '1px solid var(--tv-border)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.recent_alerts.length === 0 && (
              <tr><td colSpan={3} style={{ padding: 20, textAlign: 'center', color: 'var(--tv-text-muted)' }}>
                No alerts in history — server has been healthy since snapshot tracking began
              </td></tr>
            )}
            {data.recent_alerts.map((a) => (
              <tr key={a.id} style={{ borderTop: '1px solid var(--tv-border-lt)' }}>
                <td style={{ padding: '10px 14px', fontFamily: 'Roboto Mono, monospace', color: 'var(--tv-text-dim)' }}>
                  {fmtUTC(a.created_at)}
                </td>
                <td style={{ padding: '10px 14px', color: 'var(--tv-bear)', fontWeight: 700 }}>
                  {Array.isArray(a.problems) ? a.problems.length : 0}
                </td>
                <td style={{ padding: '10px 14px', color: 'var(--tv-text)', fontSize: 11 }}>
                  {Array.isArray(a.problems) && a.problems.length > 0
                    ? a.problems.slice(0, 2).join(' · ') + (a.problems.length > 2 ? ` (+${a.problems.length - 2} more)` : '')
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
